/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片选择器状态管理
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-24
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// 水印状态类型
type WatermarkStatus =
  | 'extracting'
  | 'completed'
  | 'failed'
  | 'extracting_timeout'
  | 'edit_completed'
  | 'edit_failed'
  | 'edit_no_changes'
  | 'edit_timeout'

// 照片类型
interface Photo {
  seq: string | number
  watermarkStatus?: WatermarkStatus
  [key: string]: any
}

// State 类型
interface PhotoPickerState {
  photos: Photo[]
  backFromPickerPage: boolean
  from: string | null
}

const initialState: PhotoPickerState = {
  photos: [],
  backFromPickerPage: false,
  from: null
}

const photoPickerSlice = createSlice({
  name: 'photoPicker',
  initialState,
  reducers: {
    setPhotos: (
      state,
      action: PayloadAction<{ photos: Photo[]; from?: string | null }>
    ) => {
      state.photos = action.payload.photos.map((photo) => ({
        ...photo,
        watermarkStatus: 'extracting' as WatermarkStatus
      }))
      state.from = action.payload.from || null
    },

    appendPhotos: (
      state,
      action: PayloadAction<{ photos: Photo[]; from?: string | null }>
    ) => {
      const existingSeqSet = new Set(state.photos.map((photo) => photo.seq))
      const newPhotos = action.payload.photos
        .filter((photo) => !existingSeqSet.has(photo.seq))
        .map((photo) => ({
          ...photo,
          watermarkStatus: 'extracting' as WatermarkStatus
        }))
      state.photos = [...state.photos, ...newPhotos]
      state.from = action.payload.from || state.from
    },

    updateAllPhotosStatus: (
      state,
      action: PayloadAction<{ photos: Photo[]; status: WatermarkStatus }>
    ) => {
      const { photos: incomingPhotos, status } = action.payload

      if (!incomingPhotos || !Array.isArray(incomingPhotos)) {
        return
      }

      const photoSeqsToUpdate = new Set(
        incomingPhotos.map((photo) => photo.seq)
      )

      state.photos = state.photos.map((photo) => {
        if (photoSeqsToUpdate.has(photo.seq)) {
          // 如果照片当前状态是'failed'或'extracting_timeout'，且新状态是编辑相关状态，则不更新
          if (
            (photo.watermarkStatus === 'failed' ||
              photo.watermarkStatus === 'extracting_timeout') &&
            (status === 'edit_completed' ||
              status === 'edit_failed' ||
              status === 'edit_no_changes' ||
              status === 'edit_timeout')
          ) {
            return photo
          }

          const incomingPhoto = incomingPhotos.find((p) => p.seq === photo.seq)

          if (incomingPhoto) {
            return {
              ...photo,
              ...incomingPhoto,
              watermarkStatus: status
            }
          }

          return {
            ...photo,
            watermarkStatus: status
          }
        }
        return photo
      })
    },

    setBackFromPickerPage: (state, action: PayloadAction<boolean>) => {
      state.backFromPickerPage = action.payload
    }
  }
})

export const {
  setPhotos,
  appendPhotos,
  updateAllPhotosStatus,
  setBackFromPickerPage
} = photoPickerSlice.actions

export default photoPickerSlice.reducer
